import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "@css/Header.css";

import { useUser } from "@context/contexts";
import { adminLinks } from "./HeaderLinks/AdminLinks.js";
import { userLinks } from "./HeaderLinks/UserLinks.js";
import MobileTabBar from "./MobileTabBar";
import SiteHealthStatus from "./SiteHealthStatus";
import SubscriptionBadge from "./SubscriptionBadge";
import TechSupportPopup from "./TechSupportPopup";
import { ImpersonationIndicator } from "./ImpersonationIndicator";
import { useFeatureFlags } from "@hooks/useFeatureFlags";

// Must match the hamburger `@media (max-width: 1280px)` breakpoint in css/Header.css.
const HAMBURGER_MAX_WIDTH = 1280;

const AdminToggle = ({ isAdminView, onToggle, label }) => (
    <li className="nav-item admin-toggle">
        <button
            className="nav-btn"
            onClick={onToggle}
        >
            {isAdminView ? (
                <img src="/images/pawn.svg" alt="user" />
            ) : (
                <img src="/images/queen.svg" alt="admin" />
            )}
            {label}
        </button>
    </li>
);

AdminToggle.propTypes = {
    isAdminView: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    label: PropTypes.string,
};

const NavItems = ({ links, toggledDropdown, toggleDropdown, onLinkClick, onSupportClick, wrapSubLabel }) =>
    links.map((item) => {
        if (item.isSentryFeedback) {
            return (
                <li key={item.label} className="nav-item">
                    <button
                        className="nav-btn"
                        onClick={() => {
                            onLinkClick();
                            onSupportClick();
                        }}
                    >
                        {item.label}
                    </button>
                </li>
            );
        }
        if (item.dropdown) {
            return (
                <li key={item.label} className="nav-item">
                    <button
                        className="nav-btn"
                        onClick={() => toggleDropdown(item.label)}
                    >
                        {item.label} ▾
                    </button>
                    {toggledDropdown === item.label && (
                        <ul className="dropdown-menu">
                            {item.dropdown.map((sub) => (
                                <li key={sub.label}>
                                    <Link
                                        to={sub.to}
                                        className={sub.disabled ? "disabled" : ""}
                                        onClick={onLinkClick}
                                    >
                                        {wrapSubLabel ? <span className="label">{sub.label}</span> : sub.label}
                                        {sub.globe ? <img
                                            className="globe"
                                            src="/images/globe.svg"
                                            alt="globe"
                                        /> : null}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </li>
            );
        }
        return (
            <li key={item.label} className="nav-item">
                <Link
                    to={item.to}
                    className={`nav-btn${item.disabled ? " disabled" : ""}`}
                    onClick={onLinkClick}
                >
                    {item.label}
                </Link>
            </li>
        );
    });

const Header = () => {
    const { userData, isAdminView, setIsAdminView, logout } = useUser();
    const { stripeBilling } = useFeatureFlags();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [toggledDropdown, setToggledDropdown] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showTechSupportPopup, setShowTechSupportPopup] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // /any/page/?support opens the support modal, ?support=chat also opens the chat
    const supportParam = new URLSearchParams(location.search).get("support");
    const isSupportOpen = showTechSupportPopup || supportParam !== null;

    const closeSupport = () => {
        setShowTechSupportPopup(false);
        if (supportParam === null) return;
        const params = new URLSearchParams(location.search);
        params.delete("support");
        const search = params.toString();
        navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > HAMBURGER_MAX_WIDTH && isMenuOpen) {
                setIsMenuOpen(false);
                document.body.style.overflow = "auto";
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isMenuOpen]);

    const closeMenu = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setIsAnimating(false);
            setIsMenuOpen(false);
        }, 500);
    };

    const toggleMenu = () => {
        if (isMenuOpen) {
            closeMenu();
        } else {
            setIsMenuOpen(true);
        }
    };

    const toggleDropdown = label =>
        setToggledDropdown(toggledDropdown === label ? null : label);

    useEffect(() => {
        const handleClickOutside = event => {
            if (
                !event.target.closest(".menu-fullscreen") &&
                !event.target.closest(".hamburger") &&
                !event.target.closest(".nav-desktop") &&
                !event.target.closest(".dropdown-menu")
            ) {
                setToggledDropdown(null);
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const handleLinkClick = () => {
        setIsMenuOpen(false);
        setToggledDropdown(null);
    };

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
    }, [isMenuOpen]);

    if (!userData) return <div>?</div>;

    const visibleLinks = (isAdminView ? adminLinks : userLinks).filter((item) => {
        if (!item.featureFlag) {
            return true;
        }

        if (item.featureFlag === "stripeBilling") {
            return stripeBilling;
        }

        return true;
    });

    return (
        <header>
            <div className="navbar">
                <div className="top-bar-mobile">
                    <button className="hamburger" onClick={toggleMenu}>
                        &#9776;
                    </button>
                </div>

                {/* Меню для ПК */}
                <ul className="nav-desktop">
                    <NavItems
                        links={visibleLinks}
                        toggledDropdown={toggledDropdown}
                        toggleDropdown={toggleDropdown}
                        onLinkClick={handleLinkClick}
                        onSupportClick={() => setShowTechSupportPopup(true)}
                        wrapSubLabel
                    />

                    {userData.role !== "user" && (
                        <AdminToggle isAdminView={isAdminView} onToggle={() => setIsAdminView(!isAdminView)} />
                    )}
                </ul>
                <div className="nav-right">
                    <SiteHealthStatus />
                    <SubscriptionBadge
                        compact
                        isOpen={false}
                        onOpen={() => navigate("/profile/?section=billing")}
                        onClose={() => {}}
                    />
                    <Link
                        to="/profile/"
                        className="nav-btn"
                        onClick={handleLinkClick}
                    >
                        <p className="username">{userData.lichessId || "?"}</p>
                    </Link>
                    <button
                        className="nav-btn"
                        onClick={() => {
                            handleLinkClick();
                            logout();
                        }}
                    >
                        Выйти
                    </button>
                </div>

                {/* Меню для мобильных */}
                <div
                    className={`menu-fullscreen ${isMenuOpen ? "open" : ""} ${
                        isAnimating ? "closing" : ""
                    }`}
                >
                    <ul className="nav-menu">
                        {" "}
                        {/* структура идентична */}
                        <NavItems
                            links={visibleLinks}
                            toggledDropdown={toggledDropdown}
                            toggleDropdown={toggleDropdown}
                            onLinkClick={handleLinkClick}
                            onSupportClick={() => setShowTechSupportPopup(true)}
                        />
                        {userData.role !== "user" && (
                            <AdminToggle isAdminView={isAdminView} onToggle={() => setIsAdminView(!isAdminView)} label="Переключить" />
                        )}
                        {/* <li className="nav-item">
                            <Link to="/profile" className="nav-btn" onClick={handleLinkClick}>
                                <p className="username">
                                    {userData.lichessId || "?"}
                                </p>
                            </Link>
                        </li>
                        <li className="nav-item">
                            <a href="/logout" className="nav-btn" onClick={handleLinkClick}>
                                Выйти
                            </a>
                        </li> */}
                    </ul>
                </div>
            </div>
            {isSupportOpen ? <TechSupportPopup
                isOpen={isSupportOpen}
                closePopup={closeSupport}
                openChat={supportParam === "chat"}
            /> : null}
            <MobileTabBar links={visibleLinks} onSupportClick={() => setShowTechSupportPopup(true)} />
            <ImpersonationIndicator />
        </header>
    );
};

export default Header;
